import { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  IconButton,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import {
  formatDistance, formatVolume, formatTime, formatNumericHours,
} from '../common/util/formatter';
import ReportFilter from './components/ReportFilter';
import { useAttributePreference } from '../common/util/preferences';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import ColumnSelect from './components/ColumnSelect';
import usePersistedState from '../common/util/usePersistedState';
import { useCatch } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import MapPositions from '../map/MapPositions';
import MapView from '../map/core/MapView';
import MapCamera from '../map/MapCamera';
import AddressValue from '../common/components/AddressValue';
import TableShimmer from '../common/components/TableShimmer';
import MapGeofence from '../map/MapGeofence';
import scheduleReport from './common/scheduleReport';
import MapScale from '../map/MapScale';
import StatusCard from "../common/components/StatusCard";
import LogoImage from '../login/LogoImage';
import { nativePostMessage } from '../common/components/NativeInterface';
import { sessionActions } from '../store';
import fetchOrThrow from '../common/util/fetchOrThrow';

const columnsArray = [
  ['startTime', 'reportStartTime'],
  ['startOdometer', 'positionOdometer'],
  ['address', 'positionAddress'],
  ['endTime', 'reportEndTime'],
  ['duration', 'reportDuration'],
  ['engineHours', 'reportEngineHours'],
  ['spentFuel', 'reportSpentFuel'],
];
const columnsMap = new Map(columnsArray);

const PrintHeader = ({ deviceName, from, to }) => {
  const t = useTranslation();

  return (
    <div style={{ padding: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <LogoImage
          style={{ height: 60 }}
          onLoad={() => {
            requestAnimationFrame(() => {
              window.print();
              nativePostMessage(`print|${t('reportStops')}.pdf`);
            });
          }}
          alt={t('companyLogo')}
        />
        <h1 style={{ fontSize: 24, margin: 0 }}>{t('reportStops')}</h1>
      </div>
      <div style={{ marginTop: 10, fontSize: 14, color: '#555' }}>
        {deviceName && (
          <div>
            <strong>{t('sharedDevice')}:</strong> {deviceName}
          </div>
        )}
        {from && (
          <div>
            <strong>{t('reportFrom')}:</strong> {new Date(from).toLocaleString()}
          </div>
        )}
        {to && (
          <div>
            <strong>{t('reportTo')}:</strong> {new Date(to).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

const StopReportPage = () => {
  const navigate = useNavigate();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const dispatch = useDispatch();

  const distanceUnit = useAttributePreference('distanceUnit');
  const volumeUnit = useAttributePreference('volumeUnit');
  const defaultDeviceId = useSelector((state) => state.devices.selectedId);
  const devices = useSelector((state) => state.devices.items);
  const printing = useSelector((state) => state.session.printing);

  const [columns, setColumns] = usePersistedState('stopColumns', ['startTime', 'endTime', 'startOdometer', 'address']);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false));
    };

    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  const onMarkerClick = useCallback(() => {
    setShowCard(true);
  }, []);

  const handleSubmit = useCatch(async ({ deviceIds, groupIds, from, to, type }) => {
    setSelectedDeviceIds(deviceIds);
    setFromDate(from);
    setToDate(to);

    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    groupIds.forEach((groupId) => query.append('groupId', groupId));

    if (type === 'export') {
      window.location.assign(`/api/reports/stops/xlsx?${query.toString()}`);
    } else if (type === 'mail') {
      // Substituído fetch por fetchOrThrow
      await fetchOrThrow(`/api/reports/stops/mail?${query.toString()}`);
    } else if (type === 'pdf') {
      dispatch(sessionActions.setPrinting(true));
    } else {
      setLoading(true);
      try {
        // Substituído fetch por fetchOrThrow
        const response = await fetchOrThrow(`/api/reports/stops?${query.toString()}`, {
          headers: { Accept: 'application/json' },
        });
        setItems(await response.json());
      } finally {
        setLoading(false);
      }
    }
  });

  const handleSchedule = useCatch(async (deviceIds, groupIds, report) => {
    report.type = 'stops';
    const error = await scheduleReport(deviceIds, groupIds, report);
    if (error) throw Error(error);
    navigate('/reports/scheduled');
  });

  const formatValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value]?.name || '';
      case 'startTime':
      case 'endTime':
        return formatTime(value, 'minutes');
      case 'startOdometer':
        return formatDistance(value, distanceUnit, t);
      case 'duration':
        return formatNumericHours(value, t);
      case 'engineHours':
        return value > 0 ? formatNumericHours(value, t) : null;
      case 'spentFuel':
        return value > 0 ? formatVolume(value, volumeUnit, t) : null;
      case 'address':
        return printing ?
          (value || '') :
          (<AddressValue latitude={item.latitude} longitude={item.longitude} originalAddress={value} />);
      default:
        return value;
    }
  };

  if (printing) {
    const deviceName = selectedDeviceIds.length === 1
      ? devices[selectedDeviceIds[0]]?.name
      : t('reportMultipleDevices');

    return (
      <div>
        <PrintHeader
          deviceName={deviceName}
          from={fromDate}
          to={toDate}
        />
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((key) => (
                <TableCell key={key}>{t(columnsMap.get(key))}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.positionId}>
                {columns.map((key) => (
                  <TableCell key={key}>
                    {formatValue(item, key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportStops']}>
      <div className={classes.container}>
        {selectedItem && (
          <div className={classes.containerMap}>
            <MapView>
              <MapGeofence />
              <MapPositions
                positions={[{
                  deviceId: selectedItem.deviceId,
                  fixTime: selectedItem.startTime,
                  latitude: selectedItem.latitude,
                  longitude: selectedItem.longitude,
                }]}
                titleField="fixTime"
                onClick={onMarkerClick}
              />
              {showCard && (
                <StatusCard
                  deviceId={selectedItem.deviceId}
                  position={{
                    deviceId: selectedItem.deviceId,
                    fixTime: selectedItem.startTime,
                    latitude: selectedItem.latitude,
                    longitude: selectedItem.longitude,
                  }}
                  onClose={() => setShowCard(false)}
                  disableActions
                />
              )}
            </MapView>
            <MapScale />
            <MapCamera
              latitude={selectedItem.latitude}
              longitude={selectedItem.longitude}
            />
          </div>
        )}
        <div className={classes.containerMain}>
          <div className={classes.header}>
            <ReportFilter
              handleSubmit={handleSubmit}
              handleSchedule={handleSchedule}
              multiDevice
              includeGroups
              loading={loading}
            >
              <ColumnSelect
                columns={columns}
                setColumns={setColumns}
                columnsArray={columnsArray}
              />
            </ReportFilter>
          </div>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.columnAction} />
                <TableCell>{t('sharedDevice')}</TableCell>
                {columns.map((key) => (
                  <TableCell key={key}>{t(columnsMap.get(key))}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? items.map((item) => (
                <TableRow key={item.positionId}>
                  <TableCell className={classes.columnAction} padding="none">
                    {selectedItem === item ? (
                      <IconButton
                        size="small"
                        onClick={() => setSelectedItem(null)}
                      >
                        <GpsFixedIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => setSelectedItem(item)}
                      >
                        <LocationSearchingIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{devices[item.deviceId]?.name || ''}</TableCell>
                  {columns.map((key) => (
                    <TableCell key={key}>
                      {formatValue(item, key)}
                    </TableCell>
                  ))}
                </TableRow>
              )) : (
                <TableShimmer
                  columns={columns.length + 2}
                  startAction
                />
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </PageLayout>
  );
};

export default StopReportPage;